import React, { useMemo } from "react"

import Paragraph from "../../../../design-system/paragraph"
import UniversalLink from "../../../../design-system/universal-link"
import { isInternalUrl } from "../../../../utils/anchor-links"
import { isBrowser } from "../../../../utils/browser"
import { useLocale } from "../../../../utils/hooks/use-locale"
import { resolveLinkHref } from "../../../seo/canonical-tag/utils"
import Badge from "../badge"
import * as styles from "./index.module.scss"

interface Props {
  href: string
  icon: React.ReactNode
  label: React.ReactNode
  text?: React.ReactNode
  target?: "_blank"
  className?: string
  badge?: React.ReactNode
  onClick?: () => void
}

const NavListItem = ({
  href,
  icon,
  label,
  text,
  target,
  className,
  onClick,
  badge,
}: Props) => {
  const locale = useLocale()
  const urlWithTrailingSlash = resolveLinkHref(href, locale)
  const children = (
    <>
      <div className={styles.iconContainer}>{icon}</div>
      <div className={styles.textContainer}>
        <Paragraph variant="1" className={styles.label}>
          {label}
          {!!badge && <Badge text={badge} />}
        </Paragraph>
        {!!text && (
          <Paragraph variant="3" className={styles.description}>
            {text}
          </Paragraph>
        )}
      </div>
    </>
  )

  const sharedProps = useMemo(
    () => ({
      className: `${styles.navItemContainer} ${className || ""}`,
      tabIndex: 0,
      target,
      onClick,
    }),
    [className, onClick, target],
  )

  if (isInternalUrl(urlWithTrailingSlash)) {
    return (
      <UniversalLink
        to={urlWithTrailingSlash}
        {...sharedProps}
        onMouseUp={() => {
          if (
            isBrowser() &&
            window.location.pathname.includes(urlWithTrailingSlash)
          ) {
            const activeEl = document.activeElement
            if (activeEl instanceof HTMLElement) {
              activeEl.blur()
            }
          }
        }}
      >
        {children}
      </UniversalLink>
    )
  }

  return (
    <UniversalLink to={urlWithTrailingSlash} {...sharedProps}>
      {children}
    </UniversalLink>
  )
}

export default NavListItem
